import { useMemo } from 'react';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { useServiceTypes } from '@/hooks/scheduler/useServiceTypes';
import { LEAD_FIELD_TARGET_LABELS, type LeadFieldTarget } from '@/lib/forms/lead-field-targets';
import type { FormItem, FormItemType } from '@/types/appointmentForm';
import type { FormSurface } from '@/types/formsEngine';
import { LEAD_PRIORITY_VALUES, LEAD_URGENCY_VALUES } from '@/types/lead';

const DONT_MAP = '__dont_map__';
const UNMAPPED_OPTION_VALUE = '__unmapped__';

const TEXT_TARGETS: LeadFieldTarget[] = ['title', 'description', 'serviceRequested'];
const CHOICE_TARGETS: LeadFieldTarget[] = ['serviceCategoryId', 'priority', 'urgency'];

/**
 * Item types whose stored answer is a plain string — `FORM_ITEM_CAPABILITIES[type].answerKind
 * === 'string'` in `@notifi/shared-types/form-items`. `resolveTextValue` in operations'
 * `support/lead-field-mapping.ts` only accepts `typeof raw === 'string'`, so these are exactly
 * the types a text target (title/description/serviceRequested) can fill from.
 */
const STRING_ANSWER_TYPES = new Set<FormItemType>(['TEXT', 'DATE', 'SINGLE_SELECT', 'BARCODE_SCAN']);

/**
 * Which `config.leadField` targets a question of this item type can actually
 * fill once an intake submission converts to a lead. Narrower than what the
 * server's `assertIntakeLeadMapping` rejects — the server only restricts
 * choice targets to SINGLE_SELECT; this also keeps the picker from offering a
 * target `resolveValue` would silently never fill (e.g. `followUpDate` on a
 * plain TEXT item, or `estimatedValue` on a MEASUREMENT's `{value, unit}`).
 *
 * | FormItemType   | answerKind  | Fillable targets                                          |
 * |----------------|-------------|------------------------------------------------------------|
 * | TEXT           | string      | title, description, serviceRequested                       |
 * | DATE           | string      | followUpDate, title, description, serviceRequested         |
 * | SINGLE_SELECT  | string      | serviceCategoryId, priority, urgency (choice, per-option),  |
 * |                |             | title, description, serviceRequested                       |
 * | BARCODE_SCAN   | string      | title, description, serviceRequested (never on INTAKE)     |
 * | NUMBER         | number      | estimatedValue                                              |
 * | CHECKBOX/YES_NO/PASS_FAIL | boolean | none                                              |
 * | MULTI_SELECT   | string[]    | none (mapper never picks one of several)                    |
 * | MEASUREMENT    | measurement | none (`Number({value,unit})` is NaN, never on INTAKE)       |
 * | TIME           | time        | none                                                        |
 * | ADDRESS        | address     | none — inferred separately as the "Service address" hint    |
 * | FILE_UPLOAD    | files       | none                                                        |
 * | PHOTO/SIGNATURE| media[]/signature | none (never on INTAKE)                                |
 */
export function availableLeadFieldTargets(type: FormItemType): LeadFieldTarget[] {
  const targets: LeadFieldTarget[] = [];
  if (type === 'SINGLE_SELECT') targets.push(...CHOICE_TARGETS);
  if (type === 'DATE') targets.push('followUpDate');
  if (type === 'NUMBER') targets.push('estimatedValue');
  if (STRING_ANSWER_TYPES.has(type)) targets.push(...TEXT_TARGETS);
  return targets;
}

function isChoiceTarget(target: LeadFieldTarget | undefined): boolean {
  return Boolean(target) && (CHOICE_TARGETS as string[]).includes(target as string);
}

/**
 * Keeps `config.leadFieldOptionMap` in step with a choice item's `options`
 * list. The server's `assertIntakeLeadMapping` rejects a saved template
 * whose option map still names an option the item no longer has, and the
 * picker only renders a value row for the item's *current* options — so a
 * stale key left behind by editing options elsewhere would be both a save
 * failure and invisible in the UI. Both editors that touch `options`
 * (add/remove/rename) call these instead of writing the option list alone.
 *
 * An empty result is returned as `{}`, not stripped to `undefined` — the map
 * key on `config` is left in place, just empty; both are equally "no
 * mappings" to `resolveChoiceValue`/`assertIntakeLeadMapping`, and keeping
 * the shape stable avoids an extra branch at every call site.
 */
export function withOptionRemoved(
  optionMap: Record<string, string> | undefined,
  removedOption: string,
): Record<string, string> | undefined {
  if (!optionMap || !(removedOption in optionMap)) return optionMap;
  const next = { ...optionMap };
  delete next[removedOption];
  return next;
}

/**
 * Moves a renamed option's mapped value to its new label. See
 * `withOptionRemoved`.
 *
 * The caller (`FormDefinitionOutlineBuilder`'s `renameOptionAt`) already
 * refuses a rename that collides with a sibling option before this is ever
 * called — but `next[toOption] = next[fromOption]` would otherwise silently
 * overwrite `toOption`'s own mapped value if that guard were ever bypassed.
 * This is the second line of defence: refuse (return the map unchanged)
 * whenever `toOption` already names a key, rather than trust the caller.
 */
export function withOptionRenamed(
  optionMap: Record<string, string> | undefined,
  fromOption: string,
  toOption: string,
): Record<string, string> | undefined {
  if (
    !optionMap ||
    fromOption === toOption ||
    !(fromOption in optionMap) ||
    toOption in optionMap
  ) {
    return optionMap;
  }
  const next = { ...optionMap };
  next[toOption] = next[fromOption]!;
  delete next[fromOption];
  return next;
}

const PRIORITY_LABELS: Record<(typeof LEAD_PRIORITY_VALUES)[number], string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
};
const PRIORITY_CHOICES = LEAD_PRIORITY_VALUES.map((value) => ({
  value,
  label: PRIORITY_LABELS[value],
}));

const URGENCY_LABELS: Record<(typeof LEAD_URGENCY_VALUES)[number], string> = {
  STANDARD: 'Standard',
  SOON: 'Soon',
  URGENT: 'Urgent',
  EMERGENCY: 'Emergency',
};
const URGENCY_CHOICES = LEAD_URGENCY_VALUES.map((value) => ({
  value,
  label: URGENCY_LABELS[value],
}));

export interface LeadFieldPickerProps {
  item: FormItem;
  /** Selects the surface-specific capabilities shown by the builder; the picker only exists on INTAKE. */
  surface: FormSurface;
  /**
   * Every lead field already claimed on this template, target → the label of
   * the question claiming it (including this item's own current target, if
   * any). The server rejects two questions racing for the same field, so a
   * target claimed by a *different* question is offered disabled with a hint
   * naming it.
   */
  usage: Partial<Record<LeadFieldTarget, string>>;
  onChange: (patch: Partial<FormItem>) => void;
}

/**
 * "Maps to lead field" — the form builder's per-question control that writes
 * `config.leadField` (and, for a choice target, `config.leadFieldOptionMap`)
 * so an intake submission's answer fills a field on the lead it converts to.
 * Renders nothing for a locked contact question (name/email/phone map to the
 * contact automatically), off the INTAKE surface, or when this item's type
 * has no fillable target at all.
 */
export function LeadFieldPicker({ item, surface, usage, onChange }: LeadFieldPickerProps) {
  const target = item.config?.leadField;
  const available = useMemo(() => availableLeadFieldTargets(item.type), [item.type]);
  const options = item.config?.options ?? item.options ?? [];
  const serviceTypesQuery = useServiceTypes({ enabled: target === 'serviceCategoryId' });
  const serviceCategories = useMemo(
    () => (serviceTypesQuery.data ?? []).filter((serviceType) => serviceType.isActive),
    [serviceTypesQuery.data],
  );

  if (surface !== 'INTAKE' || item.config?.locked || available.length === 0) return null;

  function setTarget(next: string) {
    if (next === DONT_MAP) {
      onChange({
        config: { ...(item.config ?? {}), leadField: undefined, leadFieldOptionMap: undefined },
      });
      return;
    }
    onChange({
      config: {
        ...(item.config ?? {}),
        leadField: next as LeadFieldTarget,
        leadFieldOptionMap: undefined,
      },
    });
  }

  function setOptionValue(option: string, value: string) {
    const nextMap = { ...(item.config?.leadFieldOptionMap ?? {}) };
    if (value === UNMAPPED_OPTION_VALUE) {
      delete nextMap[option];
    } else {
      nextMap[option] = value;
    }
    onChange({ config: { ...(item.config ?? {}), leadFieldOptionMap: nextMap } });
  }

  const choices =
    target === 'priority'
      ? PRIORITY_CHOICES
      : target === 'urgency'
        ? URGENCY_CHOICES
        : target === 'serviceCategoryId'
          ? serviceCategories.map((serviceType) => ({ value: serviceType.id, label: serviceType.name }))
          : [];

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-[10px] text-muted-foreground">Maps to lead field</Label>
        <Select value={target ?? DONT_MAP} onValueChange={setTarget}>
          <SelectTrigger className="h-8 text-xs" aria-label="Maps to lead field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DONT_MAP}>Don&apos;t map</SelectItem>
            {available.map((candidate) => {
              const claimedBy = candidate !== target ? usage[candidate] : undefined;
              return (
                <SelectItem key={candidate} value={candidate} disabled={Boolean(claimedBy)}>
                  {LEAD_FIELD_TARGET_LABELS[candidate]}
                  {claimedBy ? ` (used by "${claimedBy}")` : ''}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {isChoiceTarget(target) && options.length > 0 ? (
        <div className="space-y-2 pl-2">
          {/* Stacked: the builder's properties panel is too narrow for the
              option name and a select side by side. */}
          {options.map((option) => (
            <div key={option} className="space-y-1">
              <p className="break-words text-[10px] text-muted-foreground" title={option}>
                {option}
              </p>
              <Select
                value={item.config?.leadFieldOptionMap?.[option] ?? UNMAPPED_OPTION_VALUE}
                onValueChange={(value) => setOptionValue(option, value)}
              >
                <SelectTrigger className="h-7 w-full text-[10px]" aria-label={`${option} maps to`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNMAPPED_OPTION_VALUE}>Don&apos;t map</SelectItem>
                  {choices.map((choice) => (
                    <SelectItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
